import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../../../hooks/useTheme';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const Stacked = ({ top, bottom, topColor, muted }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: 700, fontSize: 12, color: topColor }}>{top || '—'}</div>
    {bottom ? <div style={{ fontSize: 11, color: muted, marginTop: 1 }}>{bottom}</div> : null}
  </div>
);

const statusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '')
    .trim()
    .toLowerCase();
  const label = statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—';
  if (s === 'contabilizado') {
    return {
      label,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'anulado') {
    return {
      label,
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  return {
    label,
    bg: isDarkMode ? '#7c2d12' : '#ffedd5',
    color: isDarkMode ? '#fdba74' : '#9a3412',
    border: isDarkMode ? '#ea580c' : '#fdba74',
  };
};

const TABLE_COLUMNS = [
  { key: 'Asiento', align: 'left', sortKey: 'entry_date' },
  { key: 'Descripción', align: 'left', sortKey: 'description' },
  { key: 'DEBE', align: 'right', sortKey: 'total_debe' },
  { key: 'HABER', align: 'right', sortKey: 'total_haber' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
  { key: '', align: 'right', sortKey: null },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'entry_date':
      return `${row.entry_date || ''} ${row.entry_code || ''}`;
    case 'description':
      return String(row.description || '').toLowerCase();
    case 'total_debe':
      return Number(row.total_debe) || 0;
    case 'total_haber':
      return Number(row.total_haber) || 0;
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Asientos / libro diario: mismo look que Table3.
 */
const AccountingJournalTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  mode = 'asientos',
  tableTitle = 'Asientos',
  subtitleOverride,
  addButtonLabel = 'Nuevo asiento',
  statusFilter = 'all',
  onStatusFilterChange,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const inputBg = ui.inputBg || ui.panel || bg;
  const inputBorder = ui.inputBorder || border;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('entry_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const headerSelectRef = useRef(null);

  const canEdit = mode === 'asientos';
  const showBulkChrome = canEdit && typeof onDelete === 'function';

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (!q) return true;
      const linesText = (row.lines || [])
        .map((l) => `${l.account_code_snapshot || ''} ${l.account_label_snapshot || ''}`)
        .join(' ');
      const haystack = [
        row.entry_code,
        row.entry_date,
        row.description,
        row.reference_code,
        row.status,
        linesText,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchQuery]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return copy;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = sortedRows.map((r) => r.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const handleSortClick = (sortKey) => {
    if (!sortKey) return;
    if (sortColumn === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(sortKey);
      setSortDir(sortKey === 'entry_date' || sortKey.startsWith('total_') ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (!columnKey) return null;
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (!onDelete || selectedIds.length === 0) return;
    if (!window.confirm(`¿Anular ${selectedIds.length} asiento(s)?`)) return;
    const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
    for (const row of selectedRows) {
      // eslint-disable-next-line no-await-in-loop
      await onDelete(row);
    }
    setSelectedIds([]);
  };

  const subtitle =
    subtitleOverride ||
    (loading
      ? 'Cargando datos...'
      : error ||
        `${filteredRows.length} asiento${filteredRows.length === 1 ? '' : 's'}`);

  const colCount = TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0);

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
        </div>
        {canEdit && onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, glosa, cuenta, referencia…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        {onStatusFilterChange ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
              Estado
            </span>
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              style={selectStyle}
            >
              <option value="all">Todos</option>
              <option value="Borrador">Borrador</option>
              <option value="Contabilizado">Contabilizado</option>
              <option value="Anulado">Anulado</option>
            </select>
          </label>
        ) : null}
        {searchQuery.trim() ? (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            style={{
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 13, color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 12px',
              borderRadius: 6,
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={14} /> Anular
          </button>
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key || 'actions'}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: col.sortKey ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  {col.key ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col.key}
                      <SortIcon columnKey={col.sortKey} />
                    </span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                  Cargando asientos…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                  {error || 'Sin asientos.'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(row.id);
                const statusTone = statusPresentation(row.status, isDarkMode);
                const expanded = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = rowHover || headerBg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = selected
                          ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})`
                          : bg;
                      }}
                      style={{
                        borderBottom: `1px solid ${border}`,
                        background: selected
                          ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})`
                          : bg,
                        cursor: 'pointer',
                      }}
                      onClick={() => setExpandedId(expanded ? null : row.id)}
                    >
                      {showBulkChrome ? (
                        <td
                          style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleRow(row.id)}
                            aria-label={`Seleccionar ${row.entry_code}`}
                          />
                        </td>
                      ) : null}
                      <td style={{ padding: `${padV}px ${padH}px` }}>
                        <Stacked
                          top={row.entry_code || '—'}
                          bottom={String(row.entry_date || '').slice(0, 10)}
                          topColor={text}
                          muted={muted}
                        />
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px` }}>
                        <Stacked
                          top={row.description || 'Sin descripción'}
                          bottom={row.reference_code || ''}
                          topColor={text}
                          muted={muted}
                        />
                      </td>
                      <td
                        style={{
                          padding: `${padV}px ${padH}px`,
                          textAlign: 'right',
                          fontSize: 12,
                          color: '#2563eb',
                          fontWeight: 600,
                        }}
                      >
                        {money(row.total_debe)}
                      </td>
                      <td
                        style={{
                          padding: `${padV}px ${padH}px`,
                          textAlign: 'right',
                          fontSize: 12,
                          color: '#059669',
                          fontWeight: 600,
                        }}
                      >
                        {money(row.total_haber)}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            padding: '3px 9px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 650,
                            color: statusTone.color,
                            background: statusTone.bg,
                            border: `1px solid ${statusTone.border}`,
                          }}
                        >
                          {statusTone.label}
                        </span>
                      </td>
                      <td
                        style={{ padding: `${padV}px ${padH}px` }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canEdit ? (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            {onEdit ? (
                              <button
                                type="button"
                                onClick={() => onEdit(row)}
                                style={{
                                  height: 28,
                                  width: 28,
                                  borderRadius: 8,
                                  border: `1px solid ${border}`,
                                  background: 'transparent',
                                  color: text,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Pencil size={13} />
                              </button>
                            ) : null}
                            {onDelete ? (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!window.confirm(`¿Anular asiento ${row.entry_code}?`)) return;
                                  onDelete(row);
                                }}
                                style={{
                                  height: 28,
                                  width: 28,
                                  borderRadius: 8,
                                  border: `1px solid ${ui.danger || '#dc2626'}`,
                                  background: 'transparent',
                                  color: ui.danger || '#dc2626',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Trash2 size={13} />
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    {expanded ? (
                      <tr style={{ background: isDarkMode ? '#0c0a09' : '#fafaf9' }}>
                        <td
                          colSpan={colCount}
                          style={{ padding: '10px 16px 14px', borderBottom: `1px solid ${border}` }}
                        >
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 12,
                              fontSize: 12,
                            }}
                          >
                            {['debe', 'haber'].map((side) => (
                              <div key={side}>
                                <p
                                  style={{
                                    margin: '0 0 6px',
                                    fontWeight: 700,
                                    color: side === 'debe' ? '#2563eb' : '#059669',
                                    letterSpacing: '0.04em',
                                    fontSize: 11,
                                  }}
                                >
                                  {side === 'debe' ? 'DEBE' : 'HABER'}
                                </p>
                                {(row.lines || [])
                                  .filter((l) => l.side === side)
                                  .map((l) => (
                                    <div
                                      key={l.id || `${side}-${l.account_code_snapshot}-${l.amount}`}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        marginBottom: 4,
                                        color: text,
                                      }}
                                    >
                                      <span>
                                        {l.account_code_snapshot} · {l.account_label_snapshot}
                                      </span>
                                      <span style={{ fontWeight: 600 }}>{money(l.amount)}</span>
                                    </div>
                                  ))}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountingJournalTable3;
